<template>
    <DModal
        :show="showModal"
        :is-awaiting-validation="true"
        :title="__('tools.testing.snapshots.compare.results_modal.title')"
        :message="__('tools.testing.snapshots.compare.results_modal.message', {'snapshot': snapshot, 'violations': violationCount})"
        is-medium
        @close="close"
    >
        <div class="modal-content px-4 mb-4">
            <PreviewReport
                v-if="applyMode === 'replace' && replaceModePreviewReport"
                :title="__('tools.testing.snapshots.compare.results_modal.replace_mode_preview.title')"
                :verdict-text="replaceModePreviewReport.isSafe
                    ? '✓ ' + __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.safe')
                    : '✗ ' + __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.unsafe', { count: replaceModePreviewReport.overwrites })"
                :verdict-safe="replaceModePreviewReport.isSafe"
                :rows="[
                    { label: __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.insertions'), count: replaceModePreviewReport.insertions, hint: __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.insertions_hint') },
                    { label: __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.overwrites'), count: replaceModePreviewReport.overwrites, hint: __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.overwrites_hint') },
                    { label: __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.identical'), count: replaceModePreviewReport.identical, hint: __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.identical_hint') },
                    { label: __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.preserved'), count: replaceModePreviewReport.preserved, hint: __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.preserved_hint') },
                ]"
                :conflicting-records="replaceModePreviewReport.conflictingRecords"
                :conflicting-records-label="__('tools.testing.snapshots.compare.results_modal.replace_mode_preview.conflicting_records')"
                :warning="replaceModePreviewReport.hasMissingSections
                    ? __('tools.testing.snapshots.compare.results_modal.replace_mode_preview.missing_sections', { sections: replaceModePreviewReport.missingSections.join(', ') })
                    : null"
            />
            <PreviewReport
                v-if="applyMode === 'insert' && insertModePreviewReport"
                :title="__('tools.testing.snapshots.compare.results_modal.insert_mode_preview.title')"
                :verdict-text="'✓ ' + __('tools.testing.snapshots.compare.results_modal.insert_mode_preview.no_overwrites')"
                :verdict-safe="true"
                :rows="[
                    { label: __('tools.testing.snapshots.compare.results_modal.insert_mode_preview.insertions'), count: insertModePreviewReport.insertions, hint: __('tools.testing.snapshots.compare.results_modal.insert_mode_preview.insertions_hint') },
                    { label: __('tools.testing.snapshots.compare.results_modal.insert_mode_preview.skipped'), count: insertModePreviewReport.skipped, hint: __('tools.testing.snapshots.compare.results_modal.insert_mode_preview.skipped_hint') },
                ]"
            />
            <ul
                v-if="violations.length > 0"
                class="violation-list"
            >
                <li
                    v-for="(item, index) in visibleViolations"
                    :key="index"
                    class="violation-item"
                >
                    <div class="violation-item__header">
                        <span class="violation-item__class">
                            {{ __('tools.testing.snapshots.compare.results_modal.table.class_and_id', {'class': item.class, 'id': item.id}) }}
                        </span>
                        <span class="violation-item__key">{{ item.key }}</span>
                    </div>
                    <div class="violation-item__row violation-item__row--app">
                        <span class="violation-item__label">{{ __('tools.testing.snapshots.compare.results_modal.table.app') }}</span>
                        <span class="violation-item__value">
                            <img
                                v-if="item.appIsPng"
                                :src="'data:image/png;base64,' + item.appValueOriginal"
                                class="violation-item__image"
                                alt=""
                            >
                            <img
                                v-else-if="item.appIsSvg"
                                :src="'data:image/svg+xml;base64,' + item.appValueOriginal"
                                class="violation-item__image"
                                alt=""
                            >
                            <template v-else>{{ item.appValue }}</template>
                        </span>
                    </div>
                    <div class="violation-item__row violation-item__row--snapshot">
                        <span class="violation-item__label">{{ __('tools.testing.snapshots.compare.results_modal.table.snapshot') }}</span>
                        <span class="violation-item__value">
                            <img
                                v-if="item.snapshotIsPng"
                                :src="'data:image/png;base64,' + item.snapshotValueOriginal"
                                class="violation-item__image"
                                alt=""
                            >
                            <img
                                v-else-if="item.snapshotIsSvg"
                                :src="'data:image/svg+xml;base64,' + item.snapshotValueOriginal"
                                class="violation-item__image"
                                alt=""
                            >
                            <template v-else>{{ item.snapshotValue }}</template>
                        </span>
                    </div>
                </li>
            </ul>
            <p
                v-if="violationsTruncated"
                class="violation-list__truncated"
            >
                {{ __('tools.testing.snapshots.compare.results_modal.truncated', {'kept': violations.length, 'total': violationCount}) }}
            </p>
            <div
                v-if="hasMore"
                class="violation-list__more"
            >
                <p class="violation-list__count">
                    {{ __('tools.testing.snapshots.compare.results_modal.showing', {'shown': visibleViolations.length, 'total': violations.length}) }}
                </p>
                <DButton
                    color="secondary"
                    icon="chevron-down"
                    :label="__('tools.testing.snapshots.compare.results_modal.show_more', {'count': Math.min(VISIBLE_STEP, violations.length - visibleViolations.length)})"
                    @click="showMore"
                />
            </div>
            <p v-else-if="violations.length === 0">
                {{ __('tools.testing.snapshots.compare.results_modal.no_violations') }}
            </p>
        </div>
    </DModal>
</template>

<script setup>

import { computed, ref, watch } from 'vue'
import { __ } from '~@/Composables/translation'
import PreviewReport from '~@/Components/Tools/Testing/Snapshots/PreviewReport'

/**
 * The violation list is rendered in windows rather than in full.
 *
 * Each violation is about ten elements, so a comparison against a large tenant — 37,187 violations was a real one —
 * asks the browser for well over a third of a million nodes in one synchronous render. It does finish, and the rows
 * are correct when it does, but it takes long enough that the tab is unresponsive throughout and the pending modal
 * stays on screen the whole time, which reads as a hang. The fetch is not the problem: the network request completes
 * quickly and this starts afterwards.
 *
 * Nobody reads 37,187 rows anyway. The counts above the list are what answers the question; the rows are for spotting
 * what kind of thing differs.
 */
const VISIBLE_STEP = 100

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    snapshot: {
        type: String,
        default: ''
    },
    violations: {
        type: Array,
        default: () => { return [] }
    },
    // The true number of differences, which is larger than violations.length when the server truncated the list.
    violationCount: {
        type: Number,
        default: 0
    },
    violationsTruncated: {
        type: Boolean,
        default: false
    },
    applyMode: {
        type: String,
        default: 'replace'
    },
    replaceModePreviewReport: {
        type: Object,
        default: null
    },
    insertModePreviewReport: {
        type: Object,
        default: null
    }
})

const emit = defineEmits(['close'])

const visibleCount = ref(VISIBLE_STEP)

const visibleViolations = computed(() => props.violations.slice(0, visibleCount.value))
const hasMore = computed(() => props.violations.length > visibleCount.value)

// A fresh comparison starts at the first window again, otherwise reopening the modal after a large result would
// immediately re-render everything the previous one had expanded to.
watch(() => props.violations, () => {
    visibleCount.value = VISIBLE_STEP
})

function showMore () {
    visibleCount.value += VISIBLE_STEP
}

function close () {
    emit('close')
}
</script>

<style lang="scss" scoped>
.modal-content {
    box-shadow: none;
}

.modal-content h2, .modal-content p {
    color: var(--cui-body-color);
    opacity: 0.6;
}

.violation-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.violation-list__more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-top: 0.75rem;
}

.violation-list__count {
    margin: 0;
    font-size: 0.875rem;
}

.violation-list__truncated {
    margin: 0.75rem 0 0;
    font-size: 0.875rem;
    font-style: italic;
    opacity: 0.7;
}

.violation-item {
    border: 1px solid var(--colorAppBorder);
    border-radius: 4px;
    overflow: hidden;
    font-size: 0.875rem;
}

.violation-item__header {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    background: var(--cui-body-bg);
    border-bottom: 1px solid var(--colorAppBorder);
}

.violation-item__class {
    font-weight: 600;
    color: var(--cui-body-color);
    opacity: 0.8;
    white-space: nowrap;
}

.violation-item__key {
    font-style: italic;
    color: var(--cui-body-color);
    opacity: 0.5;
    font-size: 0.8125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.violation-item__row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.3rem 0.75rem;
    border-left: 3px solid transparent;

    &--app {
        border-left-color: #e55353;
    }

    &--snapshot {
        border-left-color: #2eb85c;
        border-top: 1px solid var(--colorAppBorder);
    }
}

.violation-item__label {
    flex-shrink: 0;
    width: 68px;
    color: var(--cui-body-color);
    opacity: 0.45;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding-top: 1px;
}

.violation-item__value {
    color: var(--cui-body-color);
    opacity: 0.85;
    word-break: break-all;
}

.violation-item__image {
    max-width: 25%;
}
</style>
